"use client";

import { useState } from "react";
import type { ExtractThoughtsRequest } from "@/generated/fastapi";
import { useAuth } from "@/state/globalStore";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import { extractThoughts } from "../../api/extractThoughts";
import useBacklogSearchSync from "../../hooks/useBacklogSearchSync";

export function useSourceExtraction() {
  const { accessToken } = useAuth();
  const { syncExtractedThoughts } = useBacklogSearchSync();
  const [waitingForExtraction, setWaitingForExtraction] = useState(false);
  const [extractingMessageId, setExtractingMessageId] = useState<number | null>(
    null,
  );

  async function handleSourceExtractionClick({
    request,
    onSuccessExtractionAction,
    messageId,
  }: {
    request: ExtractThoughtsRequest;
    onSuccessExtractionAction?: () => void;
    messageId?: number;
  }) {
    setWaitingForExtraction(true);
    if (messageId) {
      setExtractingMessageId(messageId);
    }

    try {
      const backendResponse = await extractThoughts(accessToken, request);

      if (backendResponse && backendResponse.status === "success") {
        const extractedThoughts = backendResponse.thoughts!;

        showSuccessToast(
          `${extractedThoughts.length} suggestions are ready to review. Confirm what belongs in your profile.`,
        );

        syncExtractedThoughts();
        onSuccessExtractionAction?.();
      } else if (
        backendResponse &&
        backendResponse.status === "validation_error"
      ) {
        showWarningToast(
          `Could not shape suggestions: ${backendResponse.message || "Unknown error."}`,
        );
      } else if (backendResponse && backendResponse.status === "no_content") {
        showWarningToast(
          "No suggestions were created. Try different options or shape from another source.",
        );
      } else if (
        backendResponse &&
        backendResponse.status === "not_implemented"
      ) {
        showWarningToast(
          "The requested extraction perspective is not yet implemented.",
        );
      } else if (backendResponse && backendResponse.status === "rate_limited") {
        showWarningToast(
          "Suggestion shaping rate limit reached. Please wait before trying again.",
        );
      } else if (backendResponse && backendResponse.status === "no_credits") {
        showWarningToast(
          "Insufficient credits to shape suggestions. Please top up your account.",
        );
      } else {
        showWarningToast("Could not shape suggestions. Please try again.");
      }

      return backendResponse;
    } catch {
      showErrorToast("Failed to shape suggestions");
      return null;
    } finally {
      setWaitingForExtraction(false);
      setExtractingMessageId(null);
    }
  }

  return {
    waitingForExtraction,
    setWaitingForExtraction,
    extractingMessageId,
    handleSourceExtractionClick,
  };
}
